"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { VERTICALS, type Vertical } from "./data";
import { POST_ART } from "./post-art";
import DemoHost from "./DemoHost";
import { IconBot, IconPhone, IconChat, IconRepeat, IconMegaphone, IconClipboard, IconCheckCircle, IconClock, IconHeart, IconSend } from "@/components/icons";

type Choice = "yes" | "no" | null;
type Phase = "chat" | "decision" | "outcome" | "done";

// Seven scenes, one story spine. Scene changes happen ONLY on Next. Typing
// animates inside the current beat; Next while typing completes the beat.
const RAIL = ["Pick", "Reception", "Caller", "Follow up", "Social", "Ops", "Report"] as const;

const SCENE_ICON: Record<number, React.ReactNode> = {
  1: <IconChat size={16} />, 2: <IconPhone size={16} />, 3: <IconRepeat size={16} />,
  4: <IconMegaphone size={16} />, 5: <IconClipboard size={16} />, 6: <IconClipboard size={16} />,
};
const SCENE_EMP: Record<number, string> = {
  1: "WhatsApp Receptionist", 2: "AI Caller", 3: "Lead Follow Up Employee",
  4: "Social Media Employee", 5: "Operations Employee", 6: "Evening report",
};

export default function DemoSim() {
  const [v, setV] = useState<Vertical | null>(null);
  const [scene, setScene] = useState(0);
  const [phase, setPhase] = useState<Phase>("chat");
  const [step, setStep] = useState(0);
  const [priceChoice, setPriceChoice] = useState<Choice>(null);
  const [fuChoice, setFuChoice] = useState<Choice>(null);
  const [post, setPost] = useState<number | null>(null);
  const [review, setReview] = useState<number | null>(null);
  const [confirmSkip, setConfirmSkip] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const [idlePulse, setIdlePulse] = useState(false);
  const [entryWave, setEntryWave] = useState(false);
  const reduced = useRef(false);
  const narratorRef = useRef<HTMLParagraphElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    reduced.current = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  // How many typing steps the current beat has.
  const typingTotal = useMemo(() => {
    if (!v) return 0;
    if (scene === 1 && phase === "chat") return v.s1.length;
    if (scene === 2) return v.call.length;
    if (scene === 3 && phase === "outcome" && fuChoice === "yes") return v.revival.length;
    if (scene === 5) return v.team.length;
    return 0;
  }, [v, scene, phase, fuChoice]);

  const typing = typingTotal > 0 && step < typingTotal;

  // The open decision, if any.
  const decisionOpen =
    (scene === 1 && phase === "decision" && priceChoice === null) ||
    (scene === 3 && phase === "decision" && fuChoice === null) ||
    (scene === 4 && phase === "decision" && post === null);

  // Typing animates inside the beat. It never changes scene or phase on its own.
  useEffect(() => {
    if (!v || review !== null || !typing) return;
    const t = setTimeout(() => setStep((s) => s + 1), reduced.current ? 120 : 1200);
    return () => clearTimeout(t);
  }, [v, typing, step, review]);

  // Idle pulse on Next after 10 seconds without input. Nothing navigates.
  useEffect(() => {
    setIdlePulse(false);
    if (!v || decisionOpen || review !== null) return;
    const t = setTimeout(() => setIdlePulse(true), 10000);
    return () => clearTimeout(t);
  }, [v, scene, phase, step, decisionOpen, review]);

  // Host entry wave on scene change, then back to context mode. Ordinary
  // transitions move no focus; the live region alone announces them.
  useEffect(() => {
    if (!v) return;
    setEntryWave(true);
    const t = setTimeout(() => setEntryWave(false), 1400);
    return () => clearTimeout(t);
  }, [scene, v]);

  // Review enter and exit relocate focus to the visible bubble sentence.
  useEffect(() => {
    if (!v) return;
    narratorRef.current?.focus({ preventScroll: true });
  }, [review, v]);

  const hostMode: "idle" | "talk" | "wave" | "pick" =
    decisionOpen ? "pick" : entryWave ? "wave" : typing ? "talk" : "idle";

  // The presenter looks at the phone while work happens on it, and back at
  // the visitor while speaking or waiting for them.
  const hostFacing: "viewer" | "stage" = typing && !decisionOpen ? "stage" : "viewer";

  // The beat is fully rendered and the story waits on the visitor: glow Next.
  const nextReady = Boolean(v) && !typing && !decisionOpen && review === null && scene > 0 && scene < 6;

  // Which phone the story shows right now.
  const side: "cust" | "owner" = useMemo(() => {
    if (scene === 1) return phase === "decision" ? "owner" : "cust";
    if (scene === 2) return "cust";
    if (scene === 3) return phase === "outcome" && fuChoice === "yes" ? "cust" : "owner";
    return "owner";
  }, [scene, phase, fuChoice]);

  // Narrator title and sentence.
  const narrator = useMemo(() => {
    if (!v) return { t: "Pick your business", s: "Everything here is a simulation with sample data." };
    if (review !== null) return { t: `Reviewing scene ${review + 1}`, s: `Your story is waiting at scene ${scene + 1}. Nothing here can be changed.` };
    if (scene === 1) {
      if (phase === "chat") return { t: "WhatsApp Receptionist", s: typing ? "A customer messages at night. Watch the reply, or tap Next to finish the chat." : "The chat is booked. Tap Next to see what lands on your phone." };
      if (decisionOpen) return { t: "Your turn", s: "The Receptionist hands this to you. Pick one to continue." };
      if (phase === "decision") return { t: "Your call is made", s: priceChoice === "yes" ? "Approved. Tap Next to watch the customer get the reply." : "Held for you. Tap Next to see what the customer is told." };
      return { t: "WhatsApp Receptionist", s: "That is the reply your choice produced. Tap Next to meet the AI Caller." };
    }
    if (scene === 2) return { t: "AI Caller", s: typing ? "It calls the enquiry back in your calling hours, introducing itself as an AI. Tap Next to finish the call." : "Booked on the call. Tap Next." };
    if (scene === 3) {
      if (decisionOpen) return { t: "Your turn", s: "The Lead Follow Up Employee hands you its plan for your own quiet enquiries. You decide." };
      if (phase === "decision") return { t: "Your call is made", s: fuChoice === "yes" ? "Approved. Tap Next to watch a quiet lead come back." : "Understood, none will be contacted. Tap Next." };
      if (fuChoice !== "yes") return { t: "Lead Follow Up Employee", s: "No quiet enquiries were contacted, exactly as you chose. Tap Next." };
      return { t: "Lead Follow Up Employee", s: typing ? "A quiet lead gets one polite reopen. Watch, or tap Next to finish." : "Revived and booked. Tap Next." };
    }
    if (scene === 4) {
      if (decisionOpen) return { t: "Your turn", s: "The Social Media Employee hands you two designs. Pick the one you like." };
      return { t: "Your call is made", s: "Scheduled after your pick. Tap Next for the evening team check." };
    }
    if (scene === 5) return { t: "Operations Employee", s: typing ? "It asks your team what got done, with proof where you choose. Tap Next to finish." : "All replies in. Tap Next for your report." };
    return { t: "Evening report", s: "One report, shaped by the choices you just made. Read it, then run it again or book the call." };
  }, [v, scene, phase, typing, decisionOpen, priceChoice, fuChoice, review]);

  const sceneDone = useMemo(() => {
    if (!v) return false;
    if (scene === 1) return phase === "outcome";
    if (scene === 2) return !typing;
    if (scene === 3) return phase === "outcome" && (fuChoice !== "yes" || !typing);
    if (scene === 4) return phase === "outcome";
    if (scene === 5) return !typing;
    return false;
  }, [v, scene, phase, typing, fuChoice]);

  function start(pick: Vertical) {
    setV(pick); setScene(1); setPhase("chat"); setStep(0);
  }
  function reset() {
    setV(null); setScene(0); setPhase("chat"); setStep(0);
    setPriceChoice(null); setFuChoice(null); setPost(null);
    setReview(null); setConfirmSkip(false); setSkipped(false);
  }

  // Next: typing aware, then phase aware, then scene aware. Hidden while a
  // decision is open, so the story cannot pass a decision without an input.
  function next() {
    if (!v || review !== null || decisionOpen) return;
    if (typing) { setStep(typingTotal); return; }
    if (scene === 1) {
      if (phase === "chat") { setPhase("decision"); return; }
      if (phase === "decision") { setPhase("outcome"); return; }
      setScene(2); setPhase("chat"); setStep(0); return;
    }
    if (scene === 2) { setScene(3); setPhase("decision"); setStep(0); return; }
    if (scene === 3) {
      if (phase === "decision") { setPhase("outcome"); setStep(0); return; }
      setScene(4); setPhase("decision"); setStep(0); return;
    }
    if (scene === 4) { setScene(5); setPhase("chat"); setStep(0); return; }
    if (scene === 5) { setScene(6); setPhase("done"); return; }
  }

  function skipToReport() {
    if (priceChoice === null) setSkipped(true);
    if (fuChoice === null) setSkipped(true);
    setScene(6); setPhase("done"); setConfirmSkip(false); setReview(null);
  }

  const reportRows = useMemo(() => {
    if (!v) return [] as string[];
    const rows: string[] = [];
    if (priceChoice === "yes") rows.push("1 price approval (yours, from this demo)");
    else if (priceChoice === "no") rows.push("1 question passed to you (your choice in this demo), reply pending");
    else rows.push("1 price question waiting for the owner (you skipped this step)");
    if (fuChoice === "yes") { rows.push("Follow ups approved for 10 am to 7 pm"); rows.push("1 quiet lead revived and booked (sample)"); }
    else if (fuChoice === "no") rows.push("Follow ups: none sent (you said not these)");
    else rows.push("Follow ups: waiting for the owner (you skipped this step)");
    if (post !== null) rows.push("1 post approved and scheduled (your pick, from this demo)");
    else rows.push("1 post choice waiting for the owner (you skipped this step)");
    rows.push("1 call made by the AI Caller in your calling hours (sample)");
    rows.push("2 team updates collected with proof (sample)");
    return rows.concat(v.reportExtra.map((r) => r + " (sample)"));
  }, [v, priceChoice, fuChoice, post]);

  // Keep chat panes scrolled to the latest message.
  useEffect(() => {
    document.querySelectorAll(".demo-sim .chat-msgs, .demo-sim .ds-cs-lines").forEach((el) => { el.scrollTop = el.scrollHeight; });
  });

  // "Re-run the demo" remounts the picker at the top of the page while the
  // visitor is still scrolled down by the report, so bring the start screen back
  // to them, clear of the sticky header and the cookie banner. Only after a run:
  // on first load the browser owns the scroll position.
  const hasRun = useRef(false);
  useEffect(() => {
    if (v) { hasRun.current = true; return; }
    if (!hasRun.current) return;
    hasRun.current = false;
    const el = rootRef.current?.querySelector(".ds-picker") as HTMLElement | null;
    if (!el) return;
    const banner = document.querySelector(".consent-banner") as HTMLElement | null;
    const bannerH = banner && getComputedStyle(banner).display !== "none"
      ? banner.getBoundingClientRect().height + 14 : 0;
    const top = Math.max(0, window.scrollY + el.getBoundingClientRect().top - 58 - bannerH - 12);
    window.scrollTo({ top, behavior: reduced.current ? "auto" : "smooth" });
    // The button that was activated has just been unmounted, so keyboard and screen
    // reader users would be left on <body>. Park focus on the picker, as the decision
    // card does, without fighting the scroll above.
    el.setAttribute("tabindex", "-1");
    el.focus({ preventScroll: true });
  }, [v]);

  // The hand guided camera: on every story transition, glide the thing the
  // visitor should look at into view, like a person turning the screen to you.
  // All maths respect the pinned occluders: on mobile the sticky narration
  // strip defines the usable viewport, so nothing can count as visible or be
  // centered while sitting underneath it. Scrolls happen only on transitions.
  useEffect(() => {
    if (!v) return;
    // One glide at the transition, then one silent correction after things
    // settle (canvas mounts, banner dismissals and interrupted smooth scrolls
    // can all knock the first glide off target).
    const run = (settle: boolean) => {
      const root = rootRef.current;
      if (!root) return;
      const mobile = window.matchMedia("(max-width: 899px)").matches;
      const behavior: ScrollBehavior = settle || reduced.current ? "auto" : "smooth";
      const centerTarget = decisionOpen ? root.querySelector(".ds-card-turn") : scene === 6 ? root.querySelector(".ds-report") : null;
      if (mobile) {
        // The strip pins at top 58 once the demo is in view, so the usable
        // viewport starts at its PINNED bottom, computed from its height, not
        // from wherever it currently sits in the document.
        const strip = root.querySelector(".ds-narr-strip");
        const inset = 58 + (strip ? strip.getBoundingClientRect().height : 120);
        const avail = window.innerHeight - inset;
        if (centerTarget) {
          const r = centerTarget.getBoundingClientRect();
          const fullyVisible = r.top >= inset && r.bottom <= window.innerHeight;
          if (!fullyVisible) {
            const want = inset + Math.max((avail - r.height) / 2, 12);
            window.scrollBy({ top: r.top - want, behavior });
          }
        } else {
          const phone = root.querySelector(".demo-phone");
          if (phone) {
            const r = phone.getBoundingClientRect();
            if (r.top < inset || r.top > inset + 120) {
              window.scrollBy({ top: r.top - inset - 44, behavior });
            }
          }
        }
      } else {
        const target = review !== null ? root.querySelector(".ds-narr-strip")
          : centerTarget || root.querySelector(".ds-narr-strip");
        if (settle) {
          const r = target?.getBoundingClientRect();
          if (r && r.top >= 0 && r.bottom <= window.innerHeight) return;
        }
        target?.scrollIntoView({ behavior, block: centerTarget ? "center" : "start" });
      }
    };
    const t1 = setTimeout(() => run(false), 280);
    const t2 = setTimeout(() => run(true), 1100);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [v, scene, decisionOpen, review, side]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus policy: quiet on ordinary transitions (the live region alone
  // announces). Focus relocates only when a decision opens, onto the visible
  // decision card, and on review enter and exit, onto the visible bubble.
  useEffect(() => {
    if (!decisionOpen) return;
    const t = setTimeout(() => {
      const card = rootRef.current?.querySelector(".ds-card-turn") as HTMLElement | null;
      if (card) {
        card.setAttribute("tabindex", "-1");
        card.focus({ preventScroll: true });
      }
    }, 320);
    return () => clearTimeout(t);
  }, [decisionOpen]);

  // The throw: when a decision opens, the presenter tosses a glowing spark
  // onto the choices, which land with a ripple and stay highlighted.
  useEffect(() => {
    if (!decisionOpen || reduced.current) return;
    const root = rootRef.current;
    if (!root) return;
    let frame = 0;
    let rippleTimer = 0;
    let comet: HTMLDivElement | null = null;
    let ripple: HTMLDivElement | null = null;
    const t = setTimeout(() => {
      // Throw from whichever character is visible: the standing presenter on
      // desktop, the peeking head on mobile.
      const fromEl = [root.querySelector(".ds-presenter"), root.querySelector(".ds-narr-head")]
        .find((el) => el instanceof HTMLElement && el.offsetParent !== null) as HTMLElement | undefined;
      const from = fromEl?.getBoundingClientRect();
      const btn = root.querySelector(".ds-card-turn .ds-actions button, .ds-card-turn .ig-pick");
      const to = btn?.getBoundingClientRect();
      if (!from || !to) return;
      const sx = from.left + from.width * 0.62, sy = from.top + from.height * 0.3;
      const ex = to.left + to.width / 2, ey = to.top + to.height / 2;
      comet = document.createElement("div");
      comet.className = "ds-comet";
      document.body.appendChild(comet);
      const t0 = performance.now(), dur = 680;
      const cx = (sx + ex) / 2, cy = Math.min(sy, ey) - 130;
      const step = (now: number) => {
        if (!comet) return;
        const p = Math.min((now - t0) / dur, 1);
        const x = (1 - p) * (1 - p) * sx + 2 * (1 - p) * p * cx + p * p * ex;
        const y = (1 - p) * (1 - p) * sy + 2 * (1 - p) * p * cy + p * p * ey;
        comet.style.transform = `translate(${x}px, ${y}px) scale(${0.7 + 0.5 * Math.sin(Math.PI * p)})`;
        if (p < 1) frame = requestAnimationFrame(step);
        else {
          comet.remove();
          comet = null;
          ripple = document.createElement("div");
          ripple.className = "ds-ripple";
          ripple.style.left = `${ex}px`;
          ripple.style.top = `${ey}px`;
          document.body.appendChild(ripple);
          rippleTimer = window.setTimeout(() => { ripple?.remove(); ripple = null; }, 700);
        }
      };
      frame = requestAnimationFrame(step);
    }, 520);
    return () => {
      clearTimeout(t);
      cancelAnimationFrame(frame);
      window.clearTimeout(rippleTimer);
      comet?.remove();
      ripple?.remove();
    };
  }, [decisionOpen]);

  // The bubble types its sentence, so the presenter feels like it is speaking.
  // Screen readers get the full sentence at once via aria-label.
  const [spoken, setSpoken] = useState("");
  useEffect(() => {
    const s = narrator.s;
    if (reduced.current) { setSpoken(s); return; }
    setSpoken("");
    let i = 0;
    const t = setInterval(() => {
      i += 2;
      setSpoken(s.slice(0, i));
      if (i >= s.length) clearInterval(t);
    }, 24);
    return () => clearInterval(t);
  }, [narrator.s]);

  // ---- renderers ----

  const shownScene = review !== null ? review : scene;
  const frozen = review !== null;

  function renderCustomerPhone(sc: number) {
    if (!v) return null;
    if (sc === 2) {
      const callStep = frozen || sc !== scene ? v.call.length : step;
      return (
        <div className="ds-callscreen">
          <div className="ds-cs-top">
            <div className="ds-cs-avatar"><IconBot size={26} /></div>
            <div className="ds-cs-name">{v.biz}</div>
            <div className="ds-cs-sub">AI Caller · {v.callLead}</div>
            <div className="ds-cs-timer">{callStep === 0 ? "Incoming call" : callStep >= v.call.length ? "Call ended" : `00:${String(Math.min(8 + callStep * 9, 44)).padStart(2, "0")}`}</div>
            {callStep > 0 && callStep < v.call.length && <span className="ds-wave big" aria-hidden="true"><i /><i /><i /><i /><i /><i /><i /><i /><i /><i /></span>}
          </div>
          <div className="ds-cs-lines">
            {v.call.slice(0, callStep).map((l, i) => (
              <p key={i} className={`ds-callline ${l.speaker}`}><b>{l.speaker === "ai" ? "AI" : "Customer"}:</b> {l.text}</p>
            ))}
            {callStep >= v.call.length && <p className="ds-done"><IconCheckCircle size={14} /> Booked on the call. A WhatsApp confirmation follows.</p>}
          </div>
        </div>
      );
    }
    if (sc === 3) {
      const revStep = frozen || sc !== scene ? v.revival.length : step;
      return (
        <div className="chat-phone">
          <div className="chat-wh">
            <div className="chat-av"><IconBot size={20} /></div>
            <div>
              <div className="chat-wh-name">{v.queue[0].name}</div>
              <div className="chat-wh-sub">Quiet lead · new conversation</div>
            </div>
          </div>
          <div className="chat-msgs">
            {v.revival.slice(0, revStep).map((m, i) => (
              <div key={i} className={`cm-row ${m.from === "user" ? "user" : "ai"}`}>
                <div className="cm-bub">{m.text}</div>
                {m.time && <div className="cm-ts">{m.time}</div>}
              </div>
            ))}
          </div>
        </div>
      );
    }
    const chatStep = frozen || sc !== scene || phase !== "chat" ? v.s1.length : step;
    return (
      <div className="chat-phone">
        <div className="chat-wh">
          <div className="chat-av"><IconBot size={20} /></div>
          <div>
            <div className="chat-wh-name">{v.biz}</div>
            <div className="chat-wh-sub">AI assistant · WhatsApp</div>
          </div>
        </div>
        <div className="chat-msgs">
          {v.s1.slice(0, chatStep).map((m, i) => (
            <div key={i} className={`cm-row ${m.from === "user" ? "user" : "ai"}`}>
              <div className="cm-bub">{m.text}</div>
              {m.time && <div className="cm-ts">{m.time}</div>}
            </div>
          ))}
          {(frozen ? true : phase === "outcome") && priceChoice !== null && (
            <div className="cm-row ai">
              <div className="cm-bub">{priceChoice === "yes" ? v.approval.approveReply : v.approval.holdReply}</div>
              <div className="cm-ts">next morning</div>
            </div>
          )}
          {!frozen && sc === scene && phase === "chat" && typing && <div className="ds-typing" aria-hidden="true"><span /><span /><span /></div>}
        </div>
      </div>
    );
  }

  function renderOwnerPhone(sc: number) {
    if (!v) return null;
    const teamStep = frozen || sc !== scene ? v.team.length : step;
    return (
      <div className="chat-phone">
        <div className="chat-wh ds-wh-owner">
          <div className="chat-av"><IconClipboard size={18} /></div>
          <div>
            <div className="chat-wh-name">Your Console</div>
            <div className="chat-wh-sub">Owner view · sample data</div>
          </div>
        </div>
        <div className="chat-msgs">
          {sc === 1 && (
            <div className={`ds-card${decisionOpen && !frozen ? " ds-card-turn" : ""}`}>
              <div className="ds-card-top">Approval needed <span className="ds-sample">Sample</span></div>
              <p className="ds-quote">Customer asked: &quot;{v.approval.quote}&quot;</p>
              <p>{v.approval.sample}</p>
              <div className="ds-actions">
                <button className={`btn btn-primary ds-btn${priceChoice === "yes" ? " ds-btn-selected" : priceChoice ? " ds-btn-dim" : ""}`} aria-pressed={priceChoice === "yes"} disabled={priceChoice !== null || frozen} onClick={() => setPriceChoice("yes")}>Approve the standard answer{priceChoice === "yes" && " ✓"}</button>
                <button className={`btn btn-ghost ds-btn${priceChoice === "no" ? " ds-btn-selected" : priceChoice ? " ds-btn-dim" : ""}`} aria-pressed={priceChoice === "no"} disabled={priceChoice !== null || frozen} onClick={() => setPriceChoice("no")}>Ask me first{priceChoice === "no" && " ✓"}</button>
              </div>
              {(priceChoice !== null || frozen) && (
                <p className="ds-done"><IconCheckCircle size={14} /> {priceChoice === "yes" ? "Approved. The AI is replying." : priceChoice === "no" ? "Held for you. The AI tells the customer you will reply." : "Waiting (skipped)."}{frozen && <span className="ds-decided">decided</span>}</p>
              )}
            </div>
          )}

          {sc === 3 && (
            <>
              <div className="ds-card">
                <div className="ds-card-top">Quiet enquiries <span className="ds-sample">Sample</span></div>
                {v.queue.map((qi) => (
                  <p key={qi.name} className="ds-qrow"><b>{qi.name}</b> · {qi.note}{fuChoice === "yes" && <span className="ds-qsent"><IconCheckCircle size={12} /> queued</span>}</p>
                ))}
              </div>
              <div className={`ds-card${decisionOpen && !frozen ? " ds-card-turn" : ""}`}>
                <div className="ds-card-top">Follow up plan <span className="ds-sample">Sample</span></div>
                <p>{v.followup.card}</p>
                <div className="ds-actions">
                  <button className={`btn btn-primary ds-btn${fuChoice === "yes" ? " ds-btn-selected" : fuChoice ? " ds-btn-dim" : ""}`} aria-pressed={fuChoice === "yes"} disabled={fuChoice !== null || frozen} onClick={() => setFuChoice("yes")}>Approve{fuChoice === "yes" && " ✓"}</button>
                  <button className={`btn btn-ghost ds-btn${fuChoice === "no" ? " ds-btn-selected" : fuChoice ? " ds-btn-dim" : ""}`} aria-pressed={fuChoice === "no"} disabled={fuChoice !== null || frozen} onClick={() => setFuChoice("no")}>Not these{fuChoice === "no" && " ✓"}</button>
                </div>
                {(fuChoice !== null || frozen) && (
                  <p className="ds-done"><IconCheckCircle size={14} /> {fuChoice === "yes" ? "Approved for tomorrow, 10 am to 7 pm." : fuChoice === "no" ? "Understood. These three will not be contacted." : "Waiting (skipped)."}{frozen && <span className="ds-decided">decided</span>}</p>
                )}
              </div>
            </>
          )}

          {sc === 4 && (
            <div className={`ds-card${decisionOpen && !frozen ? " ds-card-turn" : ""}`}>
              <div className="ds-card-top">Two designs ready <span className="ds-sample">Sample</span></div>
              <p>The Social Media Employee made two posts for tomorrow. Pick one to schedule:</p>
              <div className="ig-options">
                {v.posts.map((p, i) => (
                  <div key={i} className={`ig-option ig-option--${i === 0 ? "a" : "b"}${post === i ? " ig-selected" : post !== null ? " ig-dim" : ""}`}>
                    <div className="ig-top">
                      <span className="ig-avatar" aria-hidden="true" />
                      <span className="ig-handle">{v.biz.toLowerCase().replace(/\s+/g, "")}</span>
                      <span className="ig-time">{post === i ? "Scheduled ✓" : "Draft"}</span>
                    </div>
                    <div className="ig-art" dangerouslySetInnerHTML={{ __html: POST_ART[v.id][i] }} />
                    <div className="ig-actions" aria-hidden="true">
                      <IconHeart size={15} /><IconChat size={15} /><IconSend size={15} />
                    </div>
                    <button className="ig-pick" aria-pressed={post === i} disabled={post !== null || frozen} onClick={() => { setPost(i); setPhase("outcome"); }}>{post === i ? "Your pick" : "Tap to pick this design"}</button>
                  </div>
                ))}
              </div>
              {(post !== null || frozen) && (
                <p className="ds-done"><IconCheckCircle size={14} /> {post !== null ? `Scheduled for 6 pm on Instagram and Facebook: "${v.posts[post]}"` : "Waiting (skipped)."}{frozen && <span className="ds-decided">decided</span>}</p>
              )}
            </div>
          )}

          {sc === 5 && (
            <div className="ds-card">
              <div className="ds-card-top">Team check, 6 pm <span className="ds-sample">Sample</span></div>
              <p className="ds-callstatus"><span className="ds-livedot" />Asking each person what got done, with proof</p>
              {v.team.slice(0, teamStep).map((l, i) => (
                <p key={i} className={`ds-teamline${l.who === "AI Employee" ? " ai" : ""}`}><b>{l.who}:</b> {l.text}</p>
              ))}
              {teamStep >= v.team.length && <p className="ds-livefoot">All replies in. One report is on the way to you.</p>}
            </div>
          )}

          {sc === 6 && (
            <div className="ds-card ds-report">
              <div className="ds-card-top">Evening report <span className="ds-sample">Sample report</span></div>
              {skipped && <p className="ds-quiet">You skipped ahead, so the choice steps show as waiting.</p>}
              <ul>{reportRows.map((r) => <li key={r}>{r}</li>)}</ul>
            </div>
          )}
        </div>
      </div>
    );
  }

  // In review, decision scenes show the owner side so the frozen decision and
  // its "decided" tag are what the visitor sees; only the call scene reviews
  // on the customer side.
  const shownSide: "cust" | "owner" = frozen ? (shownScene === 2 ? "cust" : "owner") : side;

  return (
    <div ref={rootRef} className={`demo-sim v2${decisionOpen && !frozen ? " ds-turn-open" : ""}${v && scene === 6 ? " ds-finished" : ""}`}>
      {/* The picker deliberately has no data-reveal: ScrollReveals observes only the
          nodes present on its first mount, so a picker remounted by "Re-run the demo"
          would stay at opacity 0. Its entrance is a CSS animation that replays on
          every mount. */}
      {!v && (
        <div className="ds-picker">
          <p className="lead center-text">Pick the business closest to yours.</p>
          <span className="ds-startcue" aria-hidden="true" />
          <div className="ds-chips">
            {VERTICALS.map((x) => (
              <button key={x.id} className="btn btn-ghost ds-chip" onClick={() => start(x)}>{x.label}</button>
            ))}
          </div>
          <p className="ds-cast">You will meet all five AI Employees:</p>
          <div className="ds-castrow">
            <span><IconChat size={15} /> WhatsApp Receptionist</span>
            <span><IconPhone size={15} /> AI Caller</span>
            <span><IconRepeat size={15} /> Lead Follow Up</span>
            <span><IconMegaphone size={15} /> Social Media</span>
            <span><IconClipboard size={15} /> Operations</span>
          </div>
          <p className="ds-honesty center-text muted">Simulation with sample data; your business sets its own fees, slots and rules.</p>
        </div>
      )}

      {v && (
        <>
          <div className="ds-rail" role="list" aria-label="Demo progress">
            {RAIL.map((r, i) => (
              <button
                key={r}
                role="listitem"
                className={`ds-rail-step${i === scene ? " cur" : ""}${i < scene ? " done" : ""}${review === i ? " reviewing" : ""}`}
                disabled={i >= scene || i === 0}
                onClick={() => { if (i < scene && i > 0) setReview(review === i ? null : i); }}
                aria-current={i === scene ? "step" : undefined}
              >
                {r}
              </button>
            ))}
          </div>

          {scene < 6 && !frozen && (
            <div className="ds-skipwrap">
              {!confirmSkip ? (
                <button className="ds-skip" onClick={() => setConfirmSkip(true)}>Skip to the sample report</button>
              ) : (
                <span className="ds-skipconfirm">
                  Skip the guided story and jump to the report?
                  <button className="ds-skip" onClick={skipToReport}>Yes, skip</button>
                  <button className="ds-skip" onClick={() => setConfirmSkip(false)}>Keep watching</button>
                </span>
              )}
            </div>
          )}

          <div className="demo-stage-shell">
            <div className="ds-presenter-col">
              <div className="ds-presenter">
                <DemoHost mode={hostMode} facing={hostFacing} fallback={SCENE_ICON[shownScene] || <IconBot size={26} />} />
                <span className="ds-presenter-floor" aria-hidden="true" />
              </div>
            </div>

            <div className="ds-stage-col">
              <div className="ds-narr-strip">
                <div className="ds-bubble" role="region" aria-label="Demo narrator">
                  <span className="ds-narrator-title">{narrator.t}</span>
                  <p className="ds-narrator-line" aria-live="polite" aria-label={narrator.s} tabIndex={-1} ref={narratorRef}>
                    <span aria-hidden="true">{spoken}</span>
                  </p>
                  <span className="ds-scenecount">Scene {shownScene + 1} of 7{SCENE_EMP[shownScene] ? ` · ${SCENE_EMP[shownScene]}` : ""}</span>
                </div>
                <div className="ds-narr-head" aria-hidden="true" inert>
                  <DemoHost mode={hostMode} facing={hostFacing} fallback={SCENE_ICON[shownScene] || <IconBot size={22} />} />
                </div>
                <div className="ds-strip-caption">
                  <span className="ds-stagelabel" aria-live="polite">
                    {shownSide === "cust" ? "The customer's phone" : "Your phone"}
                  </span>
                  <span className="ds-ailabel">AI assistant, introduced to the customer</span>
                </div>
              </div>
              <div className={`ds-stage one${shownSide === "owner" ? " owner-side" : ""}`}>
                <div className="ds-phone active" key={`${shownScene}-${shownSide}-${frozen ? "r" : "l"}`}>
                  <div className="demo-phone">
                    <div className="demo-phone__screen">
                      {shownSide === "cust" ? renderCustomerPhone(shownScene) : renderOwnerPhone(shownScene)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="ds-controls">
            {frozen ? (
              <button className="btn ds-next ds-next-ready" onClick={() => setReview(null)}>
                Return to where you were
              </button>
            ) : scene < 6 ? (
              <button
                className={`btn ds-next${nextReady ? " ds-next-ready" : ""}${idlePulse ? " pulse" : ""}${decisionOpen ? " ds-next-hidden" : ""}`}
                onClick={next}
                aria-hidden={decisionOpen}
                tabIndex={decisionOpen ? -1 : 0}
              >
                {typing ? "Finish this beat" : "Next"}
                <span className="ds-next-arrow" aria-hidden="true">→</span>
              </button>
            ) : (
              <div className="ds-final-actions">
                <a href="/apply/" data-lead-cta data-lead-source="demo-finish" className="btn btn-primary btn-lg ds-final-book">Book a free call</a>
                <button type="button" className="btn ds-restart" onClick={reset}>Re-run the demo</button>
              </div>
            )}
          </div>

          {scene === 6 && !frozen && (
            <div className="ds-closing" data-reveal>
              <h2 className="h2 center-text">That was a sample day, at your pace.</h2>
              <p className="center-text muted" style={{ maxWidth: 640, margin: "10px auto 0" }}>
                You met all five AI Employees: the WhatsApp Receptionist, the AI Caller, Lead Follow Up,
                Social Media and Operations. A real setup is scoped with you and runs on channels and
                hosting agreed during onboarding. Each employee runs at the autonomy level you
                choose. <a href="/ai-employees/#governance">See how that works</a>. Want this running
                in your business? Book the free call and get your written quote, confirmed before setup.
              </p>
              <div className="ds-cta">
                <a href="/apply/" data-lead-cta data-lead-source="demo" className="btn btn-primary btn-lg">Book a free call</a>
                <a href="/ai-employees/" className="btn btn-ghost btn-lg">See the employees we build</a>
              </div>
              <p className="center-text muted ds-honesty">Simulation with sample data. Not a recording of a real customer.</p>
              <p className="center-text"><button className="ds-skip" onClick={reset}>Run it again with another business</button></p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
